import { IRetailer, Retailer } from './Retailer';
import { Location } from './Location';
import { ICard, mapFromBedCard } from './Card';
import { ICallToAction } from './CallToActionTypes';
import { IBedData } from './BedData';
import { PromotionCarouselItem } from './PromotionCarouselItem';

export enum MxrType {
    MEAL = 'meal',
    PROJECT = 'project',
}

export interface IMxrConfig {
    id: string,
    mxrType: MxrType,
    size: string,
    theme: string,
    url: string,
    host: string,
    apiHost: string,
    cards: ICard[],
    tab1Label: string,
    tab2Label: string,
    tab3Label: string,
    retailer: IRetailer,
    integratedRetailer: string,
    promotions: PromotionCarouselItem[],
    bumpAnimation: boolean,
    randomizeOffersDisplayed: boolean,
    randomizeCardsDisplayed: boolean,
    showShoppingList: boolean,
    swapEnabled: boolean,
    storePickerEnabled: boolean,
    showCheckoutTotal: boolean,
    footerCallToAction: ICallToAction,
    tab3CallToAction: ICallToAction,
    customFooterEnabled: boolean,
    customFooterText: string,
    retailerIntegration: boolean,
    footerLogo: string,
    maxStoreLocation: number,
}

export class MxrConfig implements IMxrConfig {
    public id: string;

    public mxrType: MxrType;

    public size: string;

    public theme: string;

    public url: string;

    public host: string;

    public apiHost: string;

    public cards: ICard[];

    public tab1Label: string;

    public tab2Label: string;

    public tab3Label: string;

    public retailer: IRetailer;

    public integratedRetailer: string;

    public promotions: PromotionCarouselItem[];

    public bumpAnimation: boolean;

    public randomizeOffersDisplayed: boolean;

    public randomizeCardsDisplayed: boolean;

    public showShoppingList: boolean;

    public swapEnabled: boolean;

    public storePickerEnabled: boolean;

    public showCheckoutTotal: boolean;

    public footerCallToAction: ICallToAction;

    public tab3CallToAction: ICallToAction;

    public customFooterEnabled: boolean;

    public customFooterText: string;

    public retailerIntegration: boolean;

    public footerLogo: string;

    public maxStoreLocation: number;

    constructor(
        id: string,
        mxrType: MxrType,
        size: string,
        url: string,
        host: string,
        apiHost: string,
        theme: string,
        cards: ICard[],
        tab1Label: string,
        tab2Label: string,
        tab3Label: string,
        bumpAnimation: boolean,
        randomizeOffersDisplayed: boolean,
        randomizeCardsDisplayed: boolean,
        showShoppingList: boolean,
        swapEnabled: boolean,
        storePickerEnabled: boolean,
        footerCallToAction: ICallToAction,
        tab3CallToAction: ICallToAction,
        retailer: IRetailer,
        integratedRetailer: string,
        promotions: PromotionCarouselItem[],
        customFooterEnabled: boolean,
        customFooterText: string,
        showCheckoutTotal: boolean,
        retailerIntegration: boolean,
        footerLogo: string,
        maxStoreLocation: number,
    ) {
        this.id = id;
        this.mxrType = mxrType;
        this.size = size;
        this.url = url;
        this.host = host;
        this.apiHost = apiHost;
        this.theme = theme;
        this.cards = cards;
        this.tab1Label = tab1Label;
        this.tab2Label = tab2Label;
        this.tab3Label = tab3Label;
        this.bumpAnimation = bumpAnimation;
        this.randomizeOffersDisplayed = randomizeOffersDisplayed;
        this.randomizeCardsDisplayed = randomizeCardsDisplayed;
        this.showShoppingList = showShoppingList;
        this.swapEnabled = swapEnabled;
        this.storePickerEnabled = storePickerEnabled;
        this.showCheckoutTotal = showCheckoutTotal || true;
        this.footerCallToAction = footerCallToAction;
        this.tab3CallToAction = tab3CallToAction;
        this.integratedRetailer = integratedRetailer;
        this.retailer = retailer || Retailer.Default();
        this.promotions = promotions || [];
        this.customFooterEnabled = (customFooterEnabled === null || customFooterEnabled === undefined)
            ? false
            : customFooterEnabled;
        this.customFooterText = customFooterText || '';
        this.retailerIntegration = retailerIntegration || false;
        this.footerLogo = footerLogo || '';
        this.maxStoreLocation = maxStoreLocation || 50;
    }

    private static getRetailer(bedData: IBedData): Retailer {
        if (bedData.retailer) {
            const { id, name, location } = bedData.retailer;

            return new Retailer(id, name, bedData.retailer.logo, location);
        }
        return new Retailer(bedData.retailerId, '', bedData.logo, Location.Default());
    }

    public static create(bedData: IBedData): MxrConfig {
        const retailer: Retailer = this.getRetailer(bedData);

        return new MxrConfig(
            bedData.id,
            (bedData.cardType === 'meal') ? MxrType.MEAL : MxrType.PROJECT,
            bedData.size,
            bedData.url,
            bedData.host,
            bedData.apiHost,
            bedData.themeLabel,
            bedData.cards.map(mapFromBedCard),
            bedData.tabLabel1,
            bedData.tabLabel2,
            bedData.tabLabel3,
            bedData.bumpAnimation,
            bedData.randomizeOffersDisplayed,
            bedData.randomizeCardDisplayed,
            bedData.showShoppingList,
            bedData.swapEnabled,
            bedData.storePickerEnabled,
            {
                ctaType: bedData.footerBtn.type,
                url: bedData.footerBtn.url,
                emailBody: bedData.footerBtn.emailBody,
                emailSubject: bedData.footerBtn.emailSubject,
                partner: bedData.footerBtn.partner,
                label: bedData.footerBtn.label,
                showCheckoutTotal: bedData.footerBtn.showCheckoutTotal,
            } as ICallToAction,
            {
                ctaType: bedData.tab3Btn.type,
                url: bedData.tab3Btn.url,
                emailBody: bedData.tab3Btn.emailBody,
                emailSubject: bedData.tab3Btn.emailSubject,
                partner: bedData.tab3Btn.partner,
                label: bedData.tab3Btn.label,
                showCheckoutTotal: bedData.tab3Btn.showCheckoutTotal,
            } as ICallToAction,
            retailer,
            bedData.integratedRetailer,
            [],
            bedData.customFooter.enabled,
            bedData.customFooter.text,
            bedData.showCheckoutTotal,
            bedData.retailerIntegration,
            bedData.footerLogo.url,
            bedData.maxStoreLocations,
        );
    }
}
